import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaName, usePersonaColor } from '../../../../hooks/usePersonaColor';
import { MarkdownPreview } from '../MarkdownPreview';
import { SPINE_BUBBLE_STAGGER_MS } from '../chatDagTokens';

/** Source Spines SP-3a — the shared two-sided conversation bubble.
 *  Extracted from SpineConversationBubble so the intent-chat panel and the
 *  spine reply renderer share one markup. A `foreman` bubble carries the
 *  persona header + accent + telemetry footer (footer fields render only when
 *  != null, so a valid 0 shows). A `user` bubble is a plain neutral bubble —
 *  no persona dot, no accent, no footer. Entry cascade preserved (per-index
 *  transitionDelay; once-on-mount rAF strips the opacity-0/translate-y-2). */
const ConversationBubble: React.FC<{
  role: 'user' | 'foreman';
  content: string;
  personaId: string | null;
  modelUsed?: string | null;
  totalTokens?: number | null;
  totalCostUsd?: number | null;
  index?: number;
}> = ({ role, content, personaId, modelUsed, totalTokens, totalCostUsd, index = 0 }) => {
  const { darkMode } = useTheme();
  const isForeman = role === 'foreman';
  // Hooks must run unconditionally; the result is only consumed for foreman.
  const personaName = usePersonaName(personaId ?? undefined);
  const color = usePersonaColor({ id: personaId ?? '', accent_color: undefined, name: personaName });
  const accent = darkMode ? color.hexDark : color.hexLight;
  const model = (modelUsed ?? '').split('/').pop();
  const hasFooter =
    isForeman && (modelUsed != null || totalCostUsd != null || totalTokens != null);

  // Cascade entry — useRef + once-on-mount effect (NOT an inline ref callback:
  // that re-fires every render + double-invokes under StrictMode). rAF defers
  // the class strip one frame so the browser commits the opacity-0 start state.
  const wrapRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => el.classList.remove('opacity-0', 'translate-y-2'));
    return () => cancelAnimationFrame(id);
  }, []);

  const frameClass = isForeman
    ? `border border-l-2 border-amber-500/60 ${darkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-800'}`
    : `border ${darkMode ? 'border-gray-600 bg-gray-700/60 text-gray-100' : 'border-gray-300 bg-gray-100 text-gray-800'}`;

  return (
    <div
      ref={wrapRef}
      data-testid="conversation-bubble"
      data-role={role}
      className={`nodrag select-none rounded-md overflow-hidden transition-all duration-300 opacity-0 translate-y-2 ${frameClass}`}
      style={{
        width: 300,
        ...(isForeman ? { borderLeftColor: accent } : {}),
        transitionDelay: `${index * SPINE_BUBBLE_STAGGER_MS}ms`,
      }}
    >
      {isForeman && (
        <div className="flex items-center gap-1.5 px-2.5 pt-2">
          <span
            data-testid="persona-dot"
            aria-hidden
            style={{ width: 8, height: 8, borderRadius: '50%', background: accent }}
          />
          <span className="font-medium text-[12px] truncate" style={{ flex: 1 }}>
            {personaName}
          </span>
        </div>
      )}
      <div className="px-2.5 py-1 text-[12px] leading-snug max-h-[180px] overflow-y-auto">
        <MarkdownPreview text={content} />
      </div>
      {hasFooter && (
        <div
          data-testid="conversation-bubble-footer"
          className={`px-2.5 pb-1.5 flex items-center gap-2 text-[9px] ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {modelUsed != null && (
            <span className={`px-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{model}</span>
          )}
          {totalTokens != null && <span>{totalTokens} tok</span>}
          {totalCostUsd != null && <span>${totalCostUsd.toFixed(4)}</span>}
        </div>
      )}
    </div>
  );
};

export default ConversationBubble;
